"use client";

import { createContext, useCallback, useContext, useRef, useState } from "react";
import Modal from "./Modal";

interface ConfirmOptions {
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface NotifyOptions {
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
}

export interface ChooseOption<T extends string> {
  value: T;
  label: React.ReactNode;
  variant?: "primary" | "warning" | "neutral";
  /** Tooltip (hard rule: every button says what it does). */
  title?: string;
}

interface ChooseOptions<T extends string> {
  title: string;
  message?: React.ReactNode;
  options: ChooseOption<T>[];
  cancelLabel?: string;
}

interface ModalState {
  open: boolean;
  title: string;
  message?: React.ReactNode;
  // Confirm-läge: en primär-knapp + ev. cancel
  confirmLabel?: string;
  cancelLabel?: string;
  // Choose-läge: lista av alternativ
  choices?: ChooseOption<string>[];
}

interface ModalContextValue {
  // Returns true if user confirmed, false if cancelled / dismissed.
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  // Single-button notification. Resolves when dismissed.
  notify: (options: NotifyOptions) => Promise<void>;
  // Multi-choice. Resolves to chosen value or null on cancel.
  choose: <T extends string>(options: ChooseOptions<T>) => Promise<T | null>;
}

const ModalContext = createContext<ModalContextValue | null>(null);

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ModalState>({
    open: false,
    title: "",
  });
  // Resolver för aktiv modal. Värdet typas brett här eftersom samma resolver
  // används av confirm (boolean) / notify (void) / choose (string|null).
  const resolverRef = useRef<((value: unknown) => void) | null>(null);

  const close = useCallback((value: unknown) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState((s) => ({ ...s, open: false }));
    resolve?.(value);
  }, []);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        resolverRef.current = (v) => resolve(Boolean(v));
        setState({
          open: true,
          title: options.title,
          message: options.message,
          confirmLabel: options.confirmLabel ?? "Ja",
          cancelLabel: options.cancelLabel ?? "Avbryt",
          choices: undefined,
        });
      }),
    [],
  );

  const notify = useCallback(
    (options: NotifyOptions) =>
      new Promise<void>((resolve) => {
        resolverRef.current = () => resolve();
        setState({
          open: true,
          title: options.title,
          message: options.message,
          confirmLabel: options.confirmLabel ?? "OK",
          cancelLabel: undefined,
          choices: undefined,
        });
      }),
    [],
  );

  const choose = useCallback(
    <T extends string>(options: ChooseOptions<T>) =>
      new Promise<T | null>((resolve) => {
        resolverRef.current = (v) =>
          resolve(v === null ? null : (v as T));
        setState({
          open: true,
          title: options.title,
          message: options.message,
          confirmLabel: undefined,
          cancelLabel: options.cancelLabel ?? "Avbryt",
          choices: options.options as ChooseOption<string>[],
        });
      }),
    [],
  );

  // Render choose-modal med flera knappar — annars vanlig Modal
  const isChoose = state.open && state.choices && state.choices.length > 0;

  return (
    <ModalContext.Provider value={{ confirm, notify, choose }}>
      {children}
      {isChoose ? (
        <ChooseModal
          open={state.open}
          title={state.title}
          message={state.message}
          choices={state.choices!}
          cancelLabel={state.cancelLabel}
          onChoose={(value) => close(value)}
          onCancel={() => close(null)}
        />
      ) : (
        <Modal
          open={state.open}
          title={state.title}
          message={state.message}
          confirmLabel={state.confirmLabel}
          cancelLabel={state.cancelLabel}
          onConfirm={() => close(true)}
          onCancel={() => close(false)}
        />
      )}
    </ModalContext.Provider>
  );
}

export function useModal(): ModalContextValue {
  const ctx = useContext(ModalContext);
  if (!ctx) {
    throw new Error("useModal must be used inside <ModalProvider>");
  }
  return ctx;
}

interface ChooseModalProps {
  open: boolean;
  title: string;
  message?: React.ReactNode;
  choices: ChooseOption<string>[];
  cancelLabel?: string;
  onChoose: (value: string) => void;
  onCancel: () => void;
}

function ChooseModal({
  open,
  title,
  message,
  choices,
  cancelLabel,
  onChoose,
  onCancel,
}: ChooseModalProps) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "var(--color-scrim)" }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-lg shadow-xl max-w-sm w-full p-6"
        style={{
          background: "var(--color-bg-card)",
          border: "1px solid var(--color-border)",
        }}
      >
        <h2 className="text-base font-semibold text-[var(--color-text)]">
          {title}
        </h2>
        {message ? (
          <div className="mt-3 text-sm text-[var(--color-text)] leading-relaxed whitespace-pre-line">
            {message}
          </div>
        ) : null}
        <div className="mt-6 flex flex-col gap-2">
          {choices.map((c) => (
            <button
              key={c.value}
              type="button"
              title={c.title}
              onClick={() => onChoose(c.value)}
              className="text-sm px-4 py-2 rounded-md transition-colors hover:brightness-110"
              style={{
                background: "transparent",
                color:
                  c.variant === "primary"
                    ? "var(--color-figur-mossa)"
                    : c.variant === "warning"
                      ? "var(--color-figur-rost)"
                      : "var(--color-text)",
                border: `1px solid ${
                  c.variant === "primary"
                    ? "var(--color-figur-mossa)"
                    : c.variant === "warning"
                      ? "var(--color-figur-rost)"
                      : "var(--color-border)"
                }`,
              }}
            >
              {c.label}
            </button>
          ))}
          {cancelLabel && (
            <button
              type="button"
              onClick={onCancel}
              className="mt-2 text-sm px-4 py-2 rounded-md transition-colors text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            >
              {cancelLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
